import React from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function MenuItemDialog({ open, onClose, menuItem }) {
  if (!menuItem) return null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const menuItemDetails = {
    "Carne Asada": {
      ingredients: [
        "Flank steak or skirt steak",
        "Lime juice",
        "Orange juice",
        "Garlic",
        "Cilantro",
        "Mexican oregano",
        "Cumin",
        "Chili powder",
      ],
      origin: "Mexico",
      description:
        "Carne Asada is a traditional Mexican grilled beef dish that originated in the northern part of Mexico. The term literally means 'grilled meat' in Spanish. The meat is marinated in citrus juices and Mexican spices, then grilled to perfection, creating a charred exterior while maintaining a juicy interior.",
    },
    "Pollo Asada": {
      ingredients: [
        "Chicken breast or thighs",
        "Lime juice",
        "Orange juice",
        "Garlic",
        "Mexican oregano",
        "Achiote paste",
        "Cumin",
      ],
      origin: "Mexico",
      description:
        "Pollo Asada is the chicken version of the popular carne asada. This Mexican grilled chicken dish features a citrus-based marinade that gives the chicken its distinctive tangy flavor. The dish has evolved to become a staple in Mexican-American cuisine.",
    },
    "Verde Enchiladas": {
      ingredients: [
        "Corn tortillas",
        "Shredded chicken",
        "Green chile sauce",
        "Tomatillos",
        "Monterey Jack cheese",
        "White onion",
        "Cilantro",
      ],
      origin: "Mexico",
      description:
        "Enchiladas Verdes originated in central Mexico. The dish gets its name from the green sauce made primarily from tomatillos. While red enchiladas were more common historically, the verde (green) variety has become equally popular in both Mexico and internationally.",
    },
    "Huevos Rancheros": {
      ingredients: [
        "Eggs",
        "Corn tortillas",
        "Ranchero sauce",
        "Black beans",
        "Avocado",
        "Queso fresco",
        "Cilantro",
      ],
      origin: "Mexico",
      description:
        "Huevos Rancheros, meaning 'rancher's eggs,' originated on Mexican farms as a hearty breakfast. The dish was traditionally served mid-morning to farm workers who needed a protein-rich meal to sustain them through their work day.",
    },
    "Classic Chicken Parm": {
      ingredients: [
        "Chicken breast",
        "Italian breadcrumbs",
        "Marinara sauce",
        "Mozzarella cheese",
        "Parmesan cheese",
        "Fresh basil",
        "Garlic",
      ],
      origin: "Italian-American",
      description:
        "Chicken Parmesan, or 'Chicken Parm,' is actually an Italian-American creation, inspired by the traditional Italian dish Melanzane alla Parmigiana (made with eggplant). It was created by Italian immigrants in the northeastern United States in the early 1950s.",
    },
    "Birria Tacos": {
      ingredients: [
        "Beef (traditionally goat)",
        "Dried chilies",
        "Mexican spices",
        "Corn tortillas",
        "Onion",
        "Cilantro",
        "Lime",
        "Consommé",
      ],
      origin: "Jalisco, Mexico",
      description:
        "Birria originated in the state of Jalisco, Mexico. While traditionally made with goat meat, the beef version has gained immense popularity, especially in the form of tacos. The meat is slow-cooked in a rich, spiced broth until tender, and the tacos are served with the consommé for dipping.",
    },
  };

  const details = menuItemDetails[menuItem.name] || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: "xl" },
          backgroundColor: "transparent",
          backgroundImage: "none",
          boxShadow: "none",
          margin: { xs: 0, md: 2 },
          maxHeight: { xs: "100%", md: "calc(100% - 64px)" },
          height: { xs: "100%", md: "auto" },
        },
      }}
    >
      <MKBox
        position="relative"
        borderRadius={{ xs: 0, md: "xl" }}
        overflow="hidden"
        backgroundColor="white"
        sx={{
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            p: { xs: 2, md: 3 },
            backgroundColor: "white",
            zIndex: 2,
          }}
        >
          <MKBox display="flex" alignItems="center" justifyContent="space-between">
            <MKBox>
              <MKTypography variant="h3" color="info">
                {menuItem.name}
              </MKTypography>
              <MKTypography variant="body2" color="text" opacity={0.8}>
                {menuItem.category}
              </MKTypography>
            </MKBox>
            <IconButton
              onClick={onClose}
              aria-label="close"
              size="large"
              sx={{
                color: "text.secondary",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "rotate(90deg)",
                  color: "info.main",
                },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MKBox>
        </DialogTitle>
        <DialogContent
          sx={{
            position: "relative",
            p: { xs: 2, md: 3 },
            overflowY: "auto",
            flex: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "4px",
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MKBox
                component="img"
                src={menuItem.image}
                alt={menuItem.name}
                width="100%"
                height={isMobile ? "200px" : "300px"}
                sx={{
                  objectFit: "cover",
                  borderRadius: "lg",
                  boxShadow: ({ boxShadows: { md } }) => md,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MKTypography variant="h5" mb={1}>
                Origin
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={2}>
                {details.origin}
              </MKTypography>

              <MKTypography variant="h5" mb={1}>
                Description
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={2}>
                {details.description}
              </MKTypography>

              <MKTypography variant="h5" mb={1}>
                Price
              </MKTypography>
              <MKTypography variant="body2" color="info" fontWeight="bold" mb={2}>
                {menuItem.price}
              </MKTypography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
              <MKTypography variant="h5" mt={2} mb={1}>
                Ingredients
              </MKTypography>
              <Grid container spacing={1}>
                {details.ingredients?.map((ingredient) => (
                  <Grid item key={ingredient}>
                    <MKBox
                      px={2}
                      py={1}
                      sx={{
                        backgroundColor: "grey.100",
                        borderRadius: "xl",
                        display: "inline-block",
                      }}
                    >
                      <MKTypography variant="body2" color="text">
                        {ingredient}
                      </MKTypography>
                    </MKBox>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </MKBox>
    </Dialog>
  );
}

MenuItemDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  menuItem: PropTypes.object,
};

export default MenuItemDialog;
